import { supabase, checkSupabaseConnection } from './supabase';
import { STRIPE_PRODUCTS } from '../stripe-config';

export async function createCheckoutSession(priceId: string, mode: 'payment' | 'subscription') {
  try {
    // Check connection first
    const isConnected = await checkSupabaseConnection();
    if (!isConnected) {
      throw new Error('Unable to connect to the server');
    }

    // Find the product by priceId
    const product = STRIPE_PRODUCTS.find(p => p.priceId === priceId);
    if (!product) {
      throw new Error(`Product with price ID ${priceId} not found`);
    }

    // Get the current URL for success and cancel URLs
    const origin = window.location.origin;
    const success_url = `${origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
    const cancel_url = `${origin}/payment-cancelled`;

    // Get the Supabase URL from environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not found');
    }

    // Get the auth token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      throw sessionError;
    }
    if (!session) {
      throw new Error('User not authenticated');
    }

    // Call the Supabase Edge Function to create a checkout session
    const response = await fetch(`${supabaseUrl}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        price_id: priceId,
        success_url,
        cancel_url,
        mode
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Checkout error response:', errorData);
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

export async function redirectToCheckout(priceId: string, mode: 'payment' | 'subscription') {
  try {
    const { url, sessionId } = await createCheckoutSession(priceId, mode);
    
    if (url) {
      window.location.href = url;
    } else {
      console.error('No checkout URL returned', { sessionId });
      throw new Error('No checkout URL returned');
    }
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
}

export async function getUserSubscription() {
  try {
    // Check connection first
    const isConnected = await checkSupabaseConnection();
    if (!isConnected) {
      throw new Error('Unable to connect to the server');
    }

    // Implement retry logic with exponential backoff
    const maxRetries = 3;
    let retryCount = 0;
    let lastError;

    while (retryCount < maxRetries) {
      try {
        // Check if we have a valid session first
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.warn(`Session error on attempt ${retryCount + 1}:`, sessionError);
          throw sessionError;
        }
        
        if (!session) {
          console.warn(`No active session on attempt ${retryCount + 1}`);
          throw new Error('No active session');
        }

        const { data, error } = await supabase
          .from('stripe_user_subscriptions')
          .select('*')
          .maybeSingle();

        if (error) {
          throw error;
        }

        return data;
      } catch (error) {
        console.warn(`Attempt ${retryCount + 1} failed to fetch subscription:`, error);
        lastError = error;
        retryCount++;
        
        if (retryCount < maxRetries) {
          // Wait before retrying with exponential backoff
          const delay = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // If we've exhausted all retries, throw the last error
    console.error('Failed to fetch user subscription after multiple retries:', lastError);
    throw lastError;
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    return null;
  }
}

export async function getUserOrders() {
  try {
    // Check connection first
    const isConnected = await checkSupabaseConnection();
    if (!isConnected) {
      throw new Error('Unable to connect to the server');
    }

    // Implement retry logic with exponential backoff
    const maxRetries = 3;
    let retryCount = 0;
    let lastError;

    while (retryCount < maxRetries) {
      try {
        // Check if we have a valid session first
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.warn(`Session error on attempt ${retryCount + 1}:`, sessionError);
          throw sessionError;
        }
        
        if (!session) {
          console.warn(`No active session on attempt ${retryCount + 1}`);
          throw new Error('No active session');
        }

        const { data, error } = await supabase
          .from('stripe_user_orders')
          .select('*')
          .order('order_date', { ascending: false });

        if (error) {
          throw error;
        }

        return data || [];
      } catch (error) {
        console.warn(`Attempt ${retryCount + 1} failed to fetch orders:`, error);
        lastError = error;
        retryCount++;
        
        if (retryCount < maxRetries) {
          // Wait before retrying with exponential backoff
          const delay = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // If we've exhausted all retries, throw the last error
    console.error('Failed to fetch user orders after multiple retries:', lastError);
    throw lastError;
  } catch (error) {
    console.error('Error fetching user orders:', error);
    return [];
  }
}

export function formatCurrency(amount: number, currency = 'EUR') {
  try {
    // Normalize currency code to uppercase if it exists
    const normalizedCurrency = currency ? currency.toUpperCase() : 'EUR';
    
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: normalizedCurrency,
    }).format(amount / 100);
  } catch (error) {
    console.error('Error formatting currency:', error);
    // Fallback formatting if there's an error
    return `${(amount / 100).toFixed(2)} ${currency ? currency.toUpperCase() : 'EUR'}`;
  }
}

export function getSubscriptionStatus(subscription: any) {
  if (!subscription) return 'inactive';
  
  if (subscription.subscription_status === 'active') {
    return 'active';
  } else if (subscription.subscription_status === 'trialing') {
    return 'trialing';
  } else if (subscription.subscription_status === 'past_due') {
    return 'past_due';
  } else if (subscription.subscription_status === 'canceled' && 
             subscription.current_period_end && 
             subscription.current_period_end * 1000 > Date.now()) {
    return 'canceled_but_active';
  } else {
    return 'inactive';
  }
}

export function isUserPremium(subscription: any, orders: any[]) {
  // Check if user has an active subscription
  const subscriptionStatus = getSubscriptionStatus(subscription);
  const hasActiveSubscription = ['active', 'trialing', 'canceled_but_active'].includes(subscriptionStatus);
  
  // Check if user has a lifetime purchase
  const hasLifetimePurchase = orders?.some(order => {
    const lifetimeProduct = STRIPE_PRODUCTS.find(p => 
      p.mode === 'payment' && 
      p.name.toLowerCase().includes('lifetime')
    );
    
    return order.payment_status === 'paid' && 
           lifetimeProduct?.priceId === order.price_id;
  });
  
  return hasActiveSubscription || hasLifetimePurchase;
}